import { CHECKBOX_POSITION, CHECKBOX_SHAPE, COLORS, Checkbox, DSTooltip } from '@repo/design-system/index'
import { QIcon_question_outline, QIcon_thumbs_up } from '@repo/icons/index'
import { useEffect } from 'react'
import { useEventTracking } from '../../../../helpers/gtmHelper'
import { useCurrentUser } from '../../../../modules/context/UserContext'
import useFilterItems from '../../../../modules/hooks/useFilterItems'
import { n_tutor } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { isB2BHRGroup } from '../../../../modules/utils'

const Divider = () => (
  <div className="flex flex-col justify-center mt-[16px]">
    <div className="shrink-0 h-px bg-gray-200" />
  </div>
)

const PointSVG = () => {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M9.78329 1.87305C9.69509 1.87305 9.60689 1.87305 9.51868 1.87795C5.15257 2.02001 1.65381 5.601 1.65381 10.0001C1.65381 14.3992 5.29468 18.1271 9.78329 18.1271C14.2719 18.1271 17.7658 14.6294 17.903 10.2646C17.903 10.1764 17.9079 10.0883 17.9079 10.0001C17.9079 5.51282 14.267 1.87305 9.77839 1.87305H9.78329ZM9.78329 16.0207C6.45604 16.0207 3.76091 13.3263 3.76091 10.0001C3.76091 7.10002 5.80921 4.68493 8.53864 4.11178C8.94045 4.0285 9.35697 3.97951 9.78329 3.97951C13.1106 3.97951 15.8057 6.67383 15.8057 10.0001C15.8057 10.4263 15.7616 10.8427 15.6734 11.2444C15.1 13.973 12.6793 16.0207 9.78329 16.0207Z"
        fill="#FEC3A8"
      />
      <path
        d="M9.78109 3.5874C9.32713 3.5874 8.8836 3.63436 8.45572 3.72829C5.54929 4.3388 3.36816 6.91649 3.36816 10.0003C3.36816 13.5434 6.23807 16.4133 9.78109 16.4133C12.8702 16.4133 15.4426 14.2321 16.0531 11.3257C16.1418 10.8978 16.194 10.4543 16.194 10.0003C16.194 6.4573 13.3241 3.5874 9.78109 3.5874V3.5874Z"
        fill="#F9725C"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.28369 13.8914C7.28369 14.2102 7.57815 14.4587 7.95587 14.4587C8.32876 14.4587 8.63895 14.2142 8.63895 13.8914V11.4293C9.03221 11.7812 9.56573 12.021 10.2683 12.021C11.0894 12.021 11.7349 11.7845 12.1722 11.3489C12.6058 10.9167 12.8165 10.3065 12.8165 9.58527V8.97776C12.8165 8.30586 12.6106 7.69753 12.1824 7.25423C11.7504 6.807 11.1056 6.54199 10.2683 6.54199C9.58525 6.54199 9.03941 6.74714 8.63682 7.0725C8.60916 6.77529 8.31527 6.54199 7.95587 6.54199C7.57339 6.54199 7.28369 6.80376 7.28369 7.11843V13.8914ZM10.0908 7.58342C10.5178 7.58342 10.8501 7.70786 11.0777 7.92773C11.3078 8.14993 11.4474 8.4855 11.4474 8.93074V9.63029C11.4474 10.0768 11.3125 10.4126 11.0875 10.6344C10.8654 10.8534 10.5394 10.9776 10.1126 10.9776C9.70603 10.9776 9.33528 10.8121 9.06278 10.5559C8.78955 10.299 8.62508 9.95973 8.62508 9.63029V8.75595C8.72925 8.09655 9.40391 7.58342 10.0908 7.58342Z"
        fill="white"
      />
    </svg>
  )
}
interface ListItemProps {
  text: string
  value?: string | number
  checkedList?: number[]
  setCheckedList?: (list: number[]) => void
}

const ListItem = ({ text, value, checkedList, setCheckedList }: ListItemProps) => {
  return (
    <div className="flex gap-[8px] p-[8px] rounded">
      <Checkbox
        shape={CHECKBOX_SHAPE.SQUARE}
        controlSelected={[checkedList, setCheckedList]}
        buttonPosition={CHECKBOX_POSITION.LEFT}
        containerClassName={'flex items-center flex-row space-x-[8px]'}
      >
        <Checkbox.Item value={value}>
          <div className="flex-1 text-black Body2-14Md">{text}</div>
        </Checkbox.Item>
      </Checkbox>
    </div>
  )
}

interface SectionProps {
  title: string
  items: ListItemProps[]
  question?: string
  checkedList: number[]
  setCheckedList: (list: number[]) => void
}

const Section = ({ title, items, question, checkedList, setCheckedList }: SectionProps) => (
  <section className="flex flex-col mt-[16px]">
    <div className="flex items-center gap-x-[8px] Body1-16Bd text-black">
      <div>{title}</div>
      {question && (
        <DSTooltip
          place={'top'}
          isMultiline={false}
          isOpenOnClick={true}
          triggerComponent={<QIcon_question_outline color={COLORS.GRAY500} width={20} height={20} />}
          contentComponent={question}
        />
      )}
    </div>
    <div className="flex flex-col mt-[8px]">
      {items.map((item, index) => (
        <ListItem key={index} {...item} checkedList={checkedList} setCheckedList={setCheckedList} />
      ))}
    </div>
  </section>
)

const Filter = ({ onChangeFilter, payload, setPayload }) => {
  const { t } = useTranslation()
  const { sendClickEvent } = useEventTracking()

  const { strengthsItems, fieldOfStudiesItems, experienceItems, accentItems, genderItems } = useFilterItems()

  useEffect(() => {
    onChangeFilter(payload)
  }, [payload])

  const handleFeaturedFilter = (data) => {
    sendClickEvent({
      button_name: 'tutor_filter',
      object_context: 'recommend_tutor',
      object_title: data.length > 0 ? 'check' : 'uncheck',
    })
    setPayload({
      ...payload,
      is_featured: data.length > 0,
    })
  }
  const handlePointbackFilter = (data) => {
    sendClickEvent({
      button_name: 'tutor_filter',
      object_context: 'rookie_tutor',
      object_title: data.length > 0 ? 'check' : 'uncheck',
    })

    setPayload({
      ...payload,
      is_pointback: data.length > 0,
    })
  }

  const handleStrengthFilter = (data) => {
    const initialArray = payload.strength
    const newArray = data
    const addedElements = newArray.filter((item) => !initialArray.includes(item))
    const removedElements = initialArray.filter((item) => !newArray.includes(item))

    let changeType = null
    let changeElement = null

    if (addedElements.length > 0) {
      changeType = 'check'
      changeElement = addedElements[0]
    } else if (removedElements.length > 0) {
      changeType = 'uncheck'
      changeElement = removedElements[0]
    } else {
      changeType = 'no change'
    }
    sendClickEvent({
      button_name: 'tutor_filter',
      object_context: strengthsItems.find((item) => item.value == changeElement)?.alt,
      object_title: changeType,
    })
    setPayload({
      ...payload,
      strength: data,
    })
  }
  const handleFiledOfStudyFilter = (data) => {
    const initialArray = payload.strength
    const newArray = data
    const addedElements = newArray.filter((item) => !initialArray.includes(item))
    const removedElements = initialArray.filter((item) => !newArray.includes(item))

    let changeType = null
    let changeElement = null

    if (addedElements.length > 0) {
      changeType = 'check'
      changeElement = addedElements[0]
    } else if (removedElements.length > 0) {
      changeType = 'uncheck'
      changeElement = removedElements[0]
    } else {
      changeType = 'no change'
    }
    sendClickEvent({
      button_name: 'tutor_filter',
      object_context: fieldOfStudiesItems.find((item) => item.value == changeElement)?.alt,
      object_title: changeType,
    })
    setPayload({
      ...payload,
      field_of_study: data,
    })
  }

  const handleExperienceFilter = (data) => {
    const initialArray = payload.strength
    const newArray = data
    const addedElements = newArray.filter((item) => !initialArray.includes(item))
    const removedElements = initialArray.filter((item) => !newArray.includes(item))

    let changeType = null
    let changeElement = null

    if (addedElements.length > 0) {
      changeType = 'check'
      changeElement = addedElements[0]
    } else if (removedElements.length > 0) {
      changeType = 'uncheck'
      changeElement = removedElements[0]
    } else {
      changeType = 'no change'
    }
    sendClickEvent({
      button_name: 'tutor_filter',
      object_context: experienceItems.find((item) => item.value == changeElement)?.alt,
      object_title: changeType,
    })
    setPayload({
      ...payload,
      experience: data,
    })
  }

  const handleGenderFilter = (data) => {
    const initialArray = payload.strength
    const newArray = data
    const addedElements = newArray.filter((item) => !initialArray.includes(item))
    const removedElements = initialArray.filter((item) => !newArray.includes(item))

    let changeType = null
    let changeElement = null

    if (addedElements.length > 0) {
      changeType = 'check'
      changeElement = addedElements[0]
    } else if (removedElements.length > 0) {
      changeType = 'uncheck'
      changeElement = removedElements[0]
    } else {
      changeType = 'no change'
    }
    sendClickEvent({
      button_name: 'tutor_filter',
      object_context: genderItems.find((item) => item.value == changeElement)?.alt,
      object_title: changeType,
    })
    setPayload({
      ...payload,
      gender: data,
    })
  }

  const handleAccentFilter = (data) => {
    const initialArray = payload.strength
    const newArray = data
    const addedElements = newArray.filter((item) => !initialArray.includes(item))
    const removedElements = initialArray.filter((item) => !newArray.includes(item))

    let changeType = null
    let changeElement = null

    if (addedElements.length > 0) {
      changeType = 'check'
      changeElement = addedElements[0]
    } else if (removedElements.length > 0) {
      changeType = 'uncheck'
      changeElement = removedElements[0]
    } else {
      changeType = 'no change'
    }
    sendClickEvent({
      button_name: 'tutor_filter',
      object_context: accentItems.find((item) => item.value == changeElement)?.alt,
      object_title: changeType,
    })
    setPayload({
      ...payload,
      accent: data,
    })
  }

  const { currentUser } = useCurrentUser()
  const isB2BUser = isB2BHRGroup(currentUser)

  return (
    <div className="flex flex-col p-16 rounded-lg border border-solid border-slate-200 max-w-[248px]">
      <section className="flex flex-col">
        <div className="flex gap-8 py-8 w-full justify-between items-center">
          <Checkbox
            shape={CHECKBOX_SHAPE.SQUARE}
            controlSelected={[payload?.is_featured ? [1] : [], handleFeaturedFilter]}
            buttonPosition={CHECKBOX_POSITION.LEFT}
            containerClassName={'flex flex-row space-x-[8px] whitespace-nowrap'}
          >
            <Checkbox.Item value={1}>
              <div className="flex flex-1 gap-1 gap-x-[4px] items-center Body1-16Bd">
                <QIcon_thumbs_up color={COLORS.PURPLE500} width={20} height={20} />
                <div className="flex-1 self-stretch text-purple-500">{t(n_tutor.category.featured)}</div>
              </div>
            </Checkbox.Item>
          </Checkbox>
          <DSTooltip
            place="top"
            isMultiline={true}
            isOpenOnClick={true}
            triggerComponent={<QIcon_question_outline color={COLORS.GRAY500} width={20} height={20} />}
            contentComponent={t(n_tutor.tooltip.featured)}
          />
        </div>
        {!isB2BUser && (
          <div className="flex gap-8 py-8 w-full justify-between items-center">
            <Checkbox
              shape={CHECKBOX_SHAPE.SQUARE}
              controlSelected={[payload?.is_pointback ? [1] : [], handlePointbackFilter]}
              buttonPosition={CHECKBOX_POSITION.LEFT}
              containerClassName={'flex flex-row space-x-[8px] whitespace-nowrap'}
            >
              <Checkbox.Item value={1}>
                <div className="flex flex-1 gap-1 gap-x-[4px] items-center Body1-16Bd">
                  <PointSVG />
                  <div className="flex-1 self-stretch text-red-400">{t(n_tutor.category.point_back)}</div>
                </div>
              </Checkbox.Item>
            </Checkbox>
            <DSTooltip
              place="bottom"
              isMultiline={true}
              isOpenOnClick={true}
              triggerComponent={<QIcon_question_outline color={COLORS.GRAY500} width={20} height={20} />}
              contentComponent={t(n_tutor.tooltip.point_back)}
            />
          </div>
        )}
      </section>
      <Divider />
      <Section
        title={t(n_tutor.category.str)}
        items={strengthsItems}
        question={t(n_tutor.tooltip.str)}
        checkedList={payload?.strength}
        setCheckedList={handleStrengthFilter}
      />
      <Divider />
      <Section
        title={t(n_tutor.category.dep)}
        items={fieldOfStudiesItems}
        checkedList={payload?.field_of_study}
        setCheckedList={handleFiledOfStudyFilter}
      />
      <Divider />
      <Section
        title={t(n_tutor.category.exp)}
        items={experienceItems}
        checkedList={payload?.experience}
        setCheckedList={handleExperienceFilter}
      />
      <Divider />
      <Section
        title={t(n_tutor.category.gen)}
        items={genderItems}
        checkedList={payload?.gender}
        setCheckedList={handleGenderFilter}
      />
      <Divider />
      <Section
        title={t(n_tutor.category.acc)}
        items={accentItems}
        checkedList={payload?.accent}
        setCheckedList={handleAccentFilter}
      />
    </div>
  )
}

export default Filter
